import { v4 as uuidv4 } from 'uuid'
import { pick, isEqual } from 'lodash'
import { createSelector } from '@reduxjs/toolkit'

import { FeatureFlag } from 'app/types'
import { RootState } from 'app/state/store'
import { waitForSelectorValue, appStoreSelect, appStoreDispatch } from 'app/state/storeUtils'
import { getFeatureFlag, launchDarklyFeatureFlagsReady } from 'features/common/state/commonSlice'

import { Map as CartoMap, CartoKeplerMapConfig, Formats, MapDataset, MapType, CartoMapLayer } from 'features/maps/types'
import { createMapDataset, deleteMapDataset, patchDataset, updateMap } from 'features/maps/api/maps'

import {
  BuilderMode,
  KeplerLayer,
  KeplerStoredVisualChannel,
  LayerTypes,
  MapDatasetEnriched,
  PopupEvent,
  PopupSettings,
  SpatialDataType,
  SpatialIndex,
  WidgetType
} from '../types'
import {
  DEFAULT_AGGREGATION_LEVEL_CLUSTER,
  DEFAULT_AGGREGATION_LEVEL_HEATMAP,
  DEFAULT_AGGREGATION_RES_LEVEL_H3,
  DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN,
  DEFAULT_GEOM_COLUMN,
  H3_RESOLUTION,
  QUADBIN_RESOLUTION
} from '../ui/builderConstants'
import { visualChannelsConfigs } from '../ui/KeplerGl/layers/visualChannels'
import { formatGeoColumn, guessGeoColumnFromSchema, parseGeoColumn } from '../utils/geoColumn'
import { datasetRequiresColumns, getDatasetColumns, isWorkflowDataset } from '../state/datasetUtils'
import { isDynamicSpatialIndexDataset } from '../state/builderSliceUtils'
import { getLayerData } from '../api/builder'
import { buildAggregationExp, getDefaultAggregationExpForLayerType } from '../state/spatialIndexUtils'
import { SchemaField, SchemaFieldType } from 'features/data-explorer/types'
import { ProviderType } from 'features/connections'
import { AGGREGATION_TYPES } from '@carto/kepler.gl'
import { MapViewState } from '@deck.gl/core'
import { getApproximateQuadbinCellSizeKm } from '../utils/quadbin'
import { getApproximateH3CellSizeKm } from '../utils/h3'
import { setCurrentMapAndDatasets } from 'features/maps/state/mapsSlice'
import { TilejsonResult } from '@carto/api-client'
import { setDynamicTilingMigrationBackup } from '../state/builderSlice'
import { isNotNullOrUndefined } from 'features/common/utils/typeUtils'

type MapDatasetWithMigrationStatus = MapDataset & {
  _migratedFromDocumentMode: boolean
}

// TODO: remove this and related code, this is just a temporary selector
// can be removed with 2024-builder-document-mode-removal-in-mem FF
export const blockAutoSaveDocMigrationTemporary = createSelector(
  (state: RootState) => state.maps.currentMapDatasets as MapDatasetWithMigrationStatus[],
  (state: RootState) => getFeatureFlag(state, FeatureFlag.BuilderDocumentModeRemovalCommit),
  (datasets, canCommit) => !canCommit && datasets.some((dataset) => dataset._migratedFromDocumentMode)
)

enum DatasetMigationStatus {
  Migrated = 'migrated',
  MigratedClone = 'migrated-clone', // migrated, but it's a clone of an original with some other spatial data settings
  Reused = 'reused', // reused as is
  Deleted = 'deleted' // deleted
}

type MigrationStatus = Map<string, DatasetMigationStatus>

/**
 * Migrate document mode layers in state.maps.currentMap and state.maps.currentMapDatasets
 * into dynamic tile layers.
 *
 * Only works on _preload_ state on builder
 */
export async function migrateDocumentModeLayers() {
  try {
    const featureFlagsLoaded = await waitForSelectorValue(launchDarklyFeatureFlagsReady, true)
      .then(() => true)
      .catch(() => false)
    if (!featureFlagsLoaded) {
      console.warn('migrateDocumentModeLayers: failed waiting for feature flags, skipping')
      return
    }

    const builderDocumentModeRemovalInMem = appStoreSelect((state) =>
      getFeatureFlag(state, FeatureFlag.BuilderDocumentModeRemovalInMem)
    )
    const builderDocumentModeRemovalCommit = appStoreSelect((state) =>
      getFeatureFlag(state, FeatureFlag.BuilderDocumentModeRemovalCommit)
    )

    const currentMap = appStoreSelect((state) => state.maps.currentMap)
    if (currentMap?.keplerMapConfig?.config?.dynamicTilingMigrationBackup) {
      appStoreDispatch(setDynamicTilingMigrationBackup(currentMap.keplerMapConfig.config.dynamicTilingMigrationBackup))
    }

    const builderDocumentModeRemoval = builderDocumentModeRemovalInMem || builderDocumentModeRemovalCommit
    if (!builderDocumentModeRemoval) return

    const builderMode = appStoreSelect((state) => state.builder.builderMode)
    const mapDatasets = appStoreSelect((state) => state.maps.currentMapDatasets)

    const oldMapConfig = currentMap?.keplerMapConfig?.config
    if (!oldMapConfig) return

    const migrationResult = await migrateMapConfigAndDatasets({
      mapConfig: oldMapConfig,
      datasets: mapDatasets
    })
    const { migrationStatus, mapConfig: newMapConfig } = migrationResult
    let newDatasets = migrationResult.datasets

    if (migrationResult.migrationStatus.size === 0) return

    const canSaveMap = builderDocumentModeRemovalCommit && builderMode === BuilderMode.Editor
    let newMap: CartoMap = { ...currentMap, keplerMapConfig: { ...currentMap.keplerMapConfig, config: newMapConfig } }

    if (canSaveMap) {
      newDatasets = await saveMigratedDatasets({
        mapId: currentMap.id,
        datasets: newDatasets,
        mapConfig: newMapConfig,
        migrationStatus
      })
      await deleteUnusedDatasets({ mapId: currentMap.id, migrationStatus })
      newMap = await updateMap(pick(newMap, ['id', 'keplerMapConfig']))
    }

    appStoreDispatch(
      setDynamicTilingMigrationBackup({
        layers: oldMapConfig.visState?.layers,
        popupSettings: oldMapConfig.popupSettings,
        widgets: oldMapConfig.widgets,
        datasets: mapDatasets.map((dataset) =>
          pick(dataset, ['id', 'type', 'source', 'providerId', 'geoColumn', 'columns', 'format'])
        )
      })
    )

    appStoreDispatch(
      setCurrentMapAndDatasets({
        map: newMap,
        datasets: newDatasets
      })
    )
  } catch (error) {
    console.error('migrateDocumentModeLayers failed', error)
    throw new Error(`Failed to migrate document mode layers and datasets: ${error}`, { cause: error })
  }
}

export async function migrateMapConfigAndDatasets({
  mapConfig,
  datasets
}: {
  mapConfig: CartoKeplerMapConfig
  datasets: MapDataset[]
}) {
  const phase1Result = migrateDocumentModeLayersAndDatasets({
    mapConfig,
    datasets
  })

  if (phase1Result.migrationStatus.size === 0) return phase1Result

  return await updateDatasetsDerivedFields({
    datasets: phase1Result.datasets,
    mapConfig: phase1Result.mapConfig,
    migrationStatus: phase1Result.migrationStatus
  })
}

/**
 * Migration Phase 1 - layer config & basic dataset migration.
 *
 * - migrate layer config,
 * - udpate or create new datasets.
 */
function migrateDocumentModeLayersAndDatasets({
  mapConfig,
  datasets
}: {
  mapConfig: CartoKeplerMapConfig
  datasets: MapDataset[]
}) {
  const migrationStatus: MigrationStatus = new Map()
  const migratedDatasets: Set<string> = new Set()

  const layers = mapConfig.visState.layers
  const newDatasets: MapDataset[] = []

  const addDataset = (dataset: MapDataset) => {
    if (newDatasets.find((d) => d.id === dataset.id)) return

    newDatasets.push(dataset)
  }

  const newLayers = layers
    .map((layer) => {
      const datasetId = layer.config.dataId

      const oldDataset = datasets.find((dataset) => dataset.id === datasetId)
      if (!oldDataset) return null

      const orphanedCandidateDatasets = datasets.filter(
        (dataset) =>
          isDatasetOrphaned(dataset, mapConfig.visState.layers) &&
          dataset.type === MapType.table &&
          dataset.format === Formats.TILEJSON &&
          dataset.source === oldDataset.source
      )

      const getMigratedDataset = (props: Partial<MapDataset>): MapDataset => {
        // search through resultDatasets and check if any of them has the same props

        const distinctProps = [...Object.keys(props)]
        const candidateDatasets = [
          ...newDatasets.filter((d) => d.source === oldDataset.source && d.type === oldDataset.type),
          ...orphanedCandidateDatasets
        ]
        for (const candidateDataset of candidateDatasets) {
          const candidateDistinctProps = pick(candidateDataset, distinctProps)
          if (isEqual(candidateDistinctProps, props)) {
            const isOrphaned = orphanedCandidateDatasets.includes(candidateDataset)
            if (isOrphaned && !migrationStatus.get(candidateDataset.id)) {
              migrationStatus.set(candidateDataset.id, DatasetMigationStatus.Reused)
            }
            addDataset(candidateDataset)
            return candidateDataset
          }
        }

        const creatingCloneDataset = newDatasets.some((d) => d.id === oldDataset.id)

        const datasetId = creatingCloneDataset ? uuidv4() : oldDataset.id
        const newDataset: MapDatasetWithMigrationStatus = {
          ...oldDataset,
          id: datasetId,
          format: Formats.TILEJSON,
          columns: null, // we'll update them in phase 2 if needed
          _migratedFromDocumentMode: true,
          ...props
        }
        migrationStatus.set(
          datasetId,
          creatingCloneDataset ? DatasetMigationStatus.MigratedClone : DatasetMigationStatus.Migrated
        )

        // Track that this dataset was part of the migration process, for its deletion
        migratedDatasets.add(oldDataset.id)

        addDataset(newDataset)
        return newDataset
      }

      const newLayer = migrateLayer({
        layer,
        oldDataset,
        mapState: mapConfig.mapState,
        getMigratedDataset: (props) => getMigratedDataset(props)
      })
      const migratedLayer = newLayer !== layer
      if (!migratedLayer) {
        addDataset(oldDataset)
      } else {
        // Track that this dataset was part of the migration process, for its deletion
        migratedDatasets.add(oldDataset.id)
      }
      return newLayer
    })
    .filter(isNotNullOrUndefined)

  mapConfig = {
    ...mapConfig,
    visState: {
      ...mapConfig.visState,
      layers: newLayers
    }
  }

  for (const dataset of datasets) {
    const wasPartOfMigration = migratedDatasets.has(dataset.id)
    if (!migrationStatus.get(dataset.id) && wasPartOfMigration) {
      migrationStatus.set(dataset.id, DatasetMigationStatus.Deleted)
    }
  }

  return {
    mapConfig,
    datasets: newDatasets,
    migrationStatus
  }
}

const isDatasetOrphaned = (dataset: MapDataset, layers: CartoMapLayer[]) => {
  // Dataset created in the workflow section comes orphaned by default and must not be migrated
  if (isWorkflowDataset(dataset)) {
    return false
  }
  return layers.every((layer) => layer.config.dataId !== dataset.id)
}

function migrateLayer({
  layer,
  oldDataset,
  mapState,
  getMigratedDataset
}: {
  layer: CartoMapLayer
  oldDataset: MapDataset
  mapState: MapViewState
  getMigratedDataset: (props: Partial<MapDataset>) => MapDataset
}): CartoMapLayer | null {
  const layerType = layer.type
  const spatialDataColumn = parseGeoColumn(oldDataset.geoColumn)?.spatialDataColumn || DEFAULT_GEOM_COLUMN

  // non-aggregated layers
  if (layerType === LayerTypes.Point || layerType === LayerTypes.Line || layerType === LayerTypes.GeoJSON) {
    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.Geo,
        spatialDataColumn
      })
    })
    const { visConfig } = layer.config
    return {
      ...layer,
      type: LayerTypes.Tileset,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...visConfig,
          stroked: Boolean(
            // point layers use outline,
            // polygons use stroked
            visConfig.outline || visConfig.stroked
          )
        }
      },
      visualChannels: {
        ...layer.visualChannels,
        radiusField: layer.visualChannels.sizeField,
        radiusScale: layer.visualChannels.sizeScale,
        sizeField: null,
        sizeScale: null
      }
    }
  } else if (layerType === LayerTypes.Grid) {
    const oldVisConfig = layer.config.visConfig

    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.Quadbin,
        spatialDataColumn
      }),
      aggregationResLevel: getAggregationResLevel({
        spatialIndex: SpatialIndex.QUADBIN,
        mapState,
        sizeKm: oldVisConfig.worldUnitSize,
        fallbackResLevel: DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN
      })
    })

    const oldVisualChannels = layer.visualChannels
    const newLayer = {
      ...layer,
      type: LayerTypes.Quadbin,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...oldVisConfig,
          filled: true,
          stroked: false,
          strokeOpacity: oldVisConfig.opacity ?? 1,
          elevationScale: oldVisConfig.elevationScale * getQuadbinElevationZoomFactor(mapState.zoom),
          // in old-doc-grid layers, the size field is used for the height
          heightRange: oldVisConfig.sizeRange,
          heightAggregation: oldVisConfig.sizeAggregation,
          sizeAggregation: undefined,
          sizeRange: null,
          worldUnitSize: undefined as unknown as number
        }
      },
      visualChannels: {
        colorField: oldVisualChannels.colorField,
        colorScale: oldVisualChannels.colorScale,
        // in old-doc-grid layers, the size field is used for the height
        heightField: oldVisualChannels.sizeField,
        heightScale: oldVisualChannels.sizeScale
      }
    }

    return newLayer
  } else if (layerType === LayerTypes.Hexagon) {
    const oldVisConfig = layer.config.visConfig
    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.H3,
        spatialDataColumn
      }),
      aggregationResLevel: getAggregationResLevel({
        spatialIndex: SpatialIndex.H3,
        mapState: mapState,
        sizeKm: oldVisConfig.worldUnitSize * 2,
        fallbackResLevel: DEFAULT_AGGREGATION_RES_LEVEL_H3
      })
    })

    const oldVisualChannels = layer.visualChannels
    const newLayer = {
      ...layer,
      type: LayerTypes.H3,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...oldVisConfig,
          filled: true,
          stroked: false,
          elevationScale: oldVisConfig.elevationScale * getHexagonElevationZoomFactor(mapState.zoom),
          worldUnitSize: undefined as unknown as number,

          // in old-doc-grid layers, the size field is used for the height
          heightRange: oldVisConfig.sizeRange,
          heightAggregation: oldVisConfig.sizeAggregation,
          sizeAggregation: undefined,
          sizeRange: null
        }
      },
      visualChannels: {
        colorField: oldVisualChannels.colorField,
        colorScale: oldVisualChannels.colorScale,
        // in old-doc-grid layers, the size field is used for the height
        heightField: oldVisualChannels.sizeField,
        heightScale: oldVisualChannels.sizeScale
      }
    }
    return newLayer
  } else if (layerType === LayerTypes.HexagonId) {
    const h3IdColumn = layer.config.columns.hex_id || 'h3'
    const oldVisConfig = layer.config.visConfig

    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.H3,
        spatialDataColumn: h3IdColumn
      }),
      aggregationResLevel: DEFAULT_AGGREGATION_RES_LEVEL_H3
    })
    const oldVisualChannels = layer.visualChannels
    const newLayer = {
      ...layer,
      type: LayerTypes.H3,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...oldVisConfig,
          filled: true,
          stroked: false,
          elevationScale: oldVisConfig.elevationScale * getHexagonElevationZoomFactor(mapState.zoom),
          worldUnitSize: undefined as unknown as number,

          // in old-doc-grid layers, the size field is used for the height
          heightRange: oldVisConfig.sizeRange,
          heightAggregation: oldVisConfig.sizeAggregation,
          sizeAggregation: undefined,
          sizeRange: null
        }
      },
      visualChannels: {
        colorField: oldVisualChannels.colorField,
        colorScale: oldVisualChannels.colorScale,
        // in old-doc-grid layers, the size field is used for the height
        heightField: oldVisualChannels.sizeField,
        heightScale: oldVisualChannels.sizeScale
      }
    }
    return newLayer
  } else if (layerType === LayerTypes.Cluster) {
    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.Quadbin,
        spatialDataColumn
      }),
      aggregationResLevel: DEFAULT_AGGREGATION_LEVEL_CLUSTER
    })
    const newLayer = {
      ...layer,
      type: LayerTypes.ClusterTile,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...layer.config.visConfig,
          filled: layer.config.visConfig?.filled ?? true,
          stroked: layer.config.visConfig?.stroked ?? false,
          isTextVisible: false
        }
      }
    }
    return newLayer
  } else if (layerType === LayerTypes.Heatmap) {
    const newDataset = getMigratedDataset({
      geoColumn: formatGeoColumn({
        spatialDataType: SpatialDataType.Quadbin,
        spatialDataColumn
      }),
      aggregationResLevel: DEFAULT_AGGREGATION_LEVEL_HEATMAP
    })
    const newLayer = {
      ...layer,
      type: LayerTypes.HeatmapTile,
      config: {
        ...layer.config,
        dataId: newDataset.id,
        visConfig: {
          ...layer.config.visConfig,
          weightAggregation: layer.config?.visConfig?.weightAggregation || 'sum',
          radius: (layer.config?.visConfig?.radius || 15) / 6
        }
      }
    }
    return newLayer
  } else if (layerType === LayerTypes.Trip) {
    return null
  }
  return layer
}
/**
 * Migration Phase 2 - update dataset and other props
 *
 * Loads schema of datasets.
 *
 * Performs following steps
 *  - update `aggregationExp` and `columns` in datasets
 *  - fixup aggregations in config
 *  - fixup popup settings (aggregations)
 */
async function updateDatasetsDerivedFields({
  datasets,
  mapConfig,
  migrationStatus
}: {
  datasets: MapDataset[]
  mapConfig: CartoKeplerMapConfig
  migrationStatus: MigrationStatus
}) {
  let popupSettings = mapConfig.popupSettings
  let widgets = mapConfig.widgets

  datasets = await Promise.all(
    datasets.map(async (dataset) => {
      if (!migrationStatus.has(dataset.id)) return dataset

      const getLayerDataParams = {
        ...dataset,
        connection: dataset.connectionName,
        provider: dataset.providerId,
        expectErrorWithSchema: true,
        mapId: 'test-map-id'
      }

      let invalidGeoColumn = false
      const layerData = await getLayerData(getLayerDataParams)
        .catch(async () => {
          // geoColumn was totally invalid, try again with default one
          invalidGeoColumn = true
          const newResult = await getLayerData({
            ...getLayerDataParams,
            geoColumn: null
          })
          return newResult
        })
        .catch(async (e) => {
          invalidGeoColumn = true
          return { schema: [], data: undefined, error: e }
        })
      if (invalidGeoColumn || layerData?.error) {
        const newGuessedGeoColumn = layerData?.schema?.length ? guessGeoColumnFromSchema(layerData.schema) : null
        dataset = {
          ...dataset,
          geoColumn: newGuessedGeoColumn
        }
        if (!newGuessedGeoColumn) {
          mapConfig.visState.layers = mapConfig.visState.layers.map((layer) => {
            if (layer.config.dataId !== dataset.id) return layer
            return {
              ...layer,
              type: LayerTypes.Unknown
            }
          })
        }
      }
      const datasetEnrichedMock: MapDatasetEnriched = {
        ...dataset,
        metadata: {
          type: MapType.tileset,
          format: dataset.format,
          geoColumn: dataset.geoColumn,
          sourceType: dataset.type,
          tilejson: layerData?.data as unknown as TilejsonResult,
          schema: layerData?.schema,
          aggregationExp: dataset.aggregationExp
        }
      } as unknown as MapDatasetEnriched

      mapConfig.visState.layers = mapConfig.visState.layers.map((layer) => {
        if (layer.config.dataId !== dataset.id) return layer

        if (
          layer.type === LayerTypes.Quadbin ||
          layer.type === LayerTypes.H3 ||
          layer.type === LayerTypes.ClusterTile
        ) {
          popupSettings =
            popupSettings &&
            popupSettingsAddDefaultAggregations({
              popupSettings,
              dataset,
              layerId: layer.id,
              schema: layerData?.schema
            })

          layer = migrateAggregations({ layer, schema: layerData?.schema, providerId: dataset.providerId })
        }
        return layer
      })

      const datasetLayerMocks = mapConfig.visState.layers
        .filter((layer) => layer.config.dataId === dataset.id)
        .map(createKeplerLayerMock)

      if (datasetRequiresColumns(dataset)) {
        return {
          ...dataset,
          columns: getDatasetColumns({
            dataset: datasetEnrichedMock,
            layers: datasetLayerMocks,
            schema: layerData?.schema,
            popupSettings,
            validFields: layerData?.schema?.map((f) => f.name)
          })
        }
      }
      if (isDynamicSpatialIndexDataset(dataset)) {
        widgets = widgets?.map((widget) => {
          if (widget.dataSource === dataset.id && widget.type === WidgetType.TIMESERIES) {
            return {
              ...widget,
              showControls: false
            }
          }
          return widget
        })
        return {
          ...dataset,
          aggregationExp: buildAggregationExp({
            layers: datasetLayerMocks,
            popupSettings: popupSettings?.layers || {},
            provider: dataset.providerId,
            defaultDatasetAggregationExpressions: datasetLayerMocks.map((l) =>
              getDefaultAggregationExpForLayerType(l.type, dataset.providerId, datasetEnrichedMock.metadata?.schema)
            )
          })
        }
      }
      return dataset
    })
  )
  return {
    datasets,
    mapConfig: {
      ...mapConfig,
      popupSettings,
      widgets
    },
    migrationStatus
  }
}

/**
 * When migrating into aggregated grid/hex/... we need to adapt aggregations:
 *
 *  * migrate count aggregations that don't have field defined in document mode layers, to count(first_field)
 *  * median -> avg
 *
 * For these, we need to know schema.
 */
function migrateAggregations({
  layer,
  schema,
  providerId
}: {
  layer: CartoMapLayer
  schema?: SchemaField[]
  providerId: ProviderType
}) {
  for (const visualChannel of Object.values(visualChannelsConfigs)) {
    const aggregationPropName = visualChannel.aggregation
    const fieldPropName = visualChannel.field
    if (!aggregationPropName || !fieldPropName) continue

    // point count aggregations don't have field defined, only aggregation
    if (
      layer.config.visConfig[aggregationPropName] === AGGREGATION_TYPES.count &&
      !layer.visualChannels[fieldPropName]
    ) {
      const numericField = schema?.find((f) => f.type === SchemaFieldType.Number)
      if (numericField) {
        layer.visualChannels = {
          ...layer.visualChannels,
          [fieldPropName]: { name: numericField.name, type: 'integer' }
        }
      } else {
        layer.config.visConfig = {
          ...layer.config.visConfig,
          [aggregationPropName]: undefined
        }
      }
    }

    // hexagonId is aggregated, but doesn't have aggregation defined in visConfig
    // default to average/mode based on field type
    if (
      layer.visualChannels[fieldPropName] &&
      typeof layer.visualChannels[fieldPropName] === 'object' &&
      !layer.config.visConfig[aggregationPropName]
    ) {
      const visChannelField = layer.visualChannels[fieldPropName] as unknown as KeplerStoredVisualChannel
      const fieldType = schema?.find((f) => f.name === visChannelField.name)?.type
      const defaultAggregation =
        fieldType === SchemaFieldType.Number ? AGGREGATION_TYPES.average : AGGREGATION_TYPES.mode
      layer.config.visConfig = {
        ...layer.config.visConfig,
        [aggregationPropName]: defaultAggregation
      }
    }

    let newVisConfig = layer.config.visConfig

    // string fields: we don't support countUnique aggregation, fallback to mode
    if (newVisConfig[aggregationPropName] === AGGREGATION_TYPES.countUnique) {
      newVisConfig = {
        ...newVisConfig,
        [aggregationPropName]: AGGREGATION_TYPES.mode
      }
    }
    // string fields: in redshift we don't support mode aggregation, fallback to anyValue
    if (providerId === ProviderType.REDSHIFT) {
      if (newVisConfig[aggregationPropName] === AGGREGATION_TYPES.mode) {
        newVisConfig = {
          ...newVisConfig,
          [aggregationPropName]: AGGREGATION_TYPES.anyValue
        }
      }
    }
    // numeric fields: we don't support median aggregation, fallback to average
    if (newVisConfig[aggregationPropName] === AGGREGATION_TYPES.median) {
      newVisConfig = {
        ...newVisConfig,
        [aggregationPropName]: AGGREGATION_TYPES.average
      }
    }
    if (newVisConfig !== layer.config.visConfig) {
      layer = {
        ...layer,
        config: {
          ...layer.config,
          visConfig: newVisConfig
        }
      }
    }
  }
  return layer
}

/**
 * Get best aggregationResLevel for dynamic tiled spatial index data source,
 * so the cell size is similar to the `sizeKm`.
 *
 * Note - it depends on `mapState`, as aggregationResLevel is relative to actual zoom
 * and depends on map position.
 */
function getAggregationResLevel({
  spatialIndex,
  mapState,
  sizeKm,
  fallbackResLevel
}: {
  spatialIndex: SpatialIndex
  mapState: MapViewState
  sizeKm: number
  fallbackResLevel: number
}) {
  const maxAggreagationResLevel = spatialIndex === SpatialIndex.QUADBIN ? QUADBIN_RESOLUTION.max : H3_RESOLUTION.max

  const sizeFunction =
    spatialIndex === SpatialIndex.QUADBIN ? getApproximateQuadbinCellSizeKm : getApproximateH3CellSizeKm

  let prevSizeKm: number | undefined = undefined
  for (let resLevel = 1; resLevel <= maxAggreagationResLevel; resLevel++) {
    const cellSizeKm = sizeFunction({ mapState, aggregationResLevel: resLevel })
    if (cellSizeKm < sizeKm) {
      if (prevSizeKm && Math.abs(prevSizeKm - sizeKm) < Math.abs(cellSizeKm - sizeKm)) return resLevel - 1
      return resLevel
    }
    prevSizeKm = cellSizeKm
  }
  return fallbackResLevel
}

/**
 * When migrating grid/hex/... we have to add default aggregations to popup field definitions.
 */
function popupSettingsAddDefaultAggregations({
  popupSettings,
  layerId,
  dataset,
  schema
}: {
  popupSettings: PopupSettings
  layerId: string
  dataset: MapDataset
  schema?: SchemaField[]
}) {
  const layer = popupSettings.layers[layerId]
  if (!layer) return popupSettings

  const providerId = dataset.providerId
  for (const event of [PopupEvent.Click, PopupEvent.Hover]) {
    const format = layer[event]
    if (!format) continue

    const newFields = format.fields
      .map((field) => {
        const schemaField = schema?.find((f) => f.name === field.name)
        if (!schemaField) return field

        const spatialIndexAggregation =
          schemaField?.type === SchemaFieldType.String
            ? providerId === ProviderType.REDSHIFT
              ? AGGREGATION_TYPES.anyValue
              : AGGREGATION_TYPES.mode
            : AGGREGATION_TYPES.sum

        return {
          ...field,
          spatialIndexAggregation
        }
      })
      // remove all those that didn't got proper aggregation
      .filter((field) => field.spatialIndexAggregation)

    popupSettings = {
      ...popupSettings,
      layers: {
        ...popupSettings.layers,
        [layerId]: {
          ...popupSettings.layers[layerId],
          [event]: {
            ...format,
            fields: newFields
          }
        }
      }
    }
  }
  return popupSettings
}

async function saveMigratedDatasets({
  mapId,
  datasets,
  mapConfig,
  migrationStatus
}: {
  mapId: string
  datasets: MapDataset[]
  mapConfig: CartoKeplerMapConfig
  migrationStatus: MigrationStatus
}) {
  return (
    await Promise.all(
      datasets.map(async (dataset) => {
        const migrationStatusValue = migrationStatus.get(dataset.id)
        if (!migrationStatusValue) return dataset

        const shouldCreateNew = migrationStatusValue === DatasetMigationStatus.MigratedClone

        if (!shouldCreateNew) {
          const patchedDataset = await patchDataset({
            mapId,
            datasetId: dataset.id,
            body: pick(dataset, ['geoColumn', 'columns', 'format', 'aggregationExp', 'aggregationResLevel'])
          })
          return { ...dataset, ...patchedDataset }
        }

        const newDataset = await createMapDataset(
          mapId,
          pick(dataset, [
            'source',
            'type',
            'connectionId',
            'geoColumn',
            'columns',
            'format',
            'aggregationExp',
            'aggregationResLevel',
            'label',
            'color'
          ])
        )
        // now touch this: whole app expects that MapDataset has connectionName & providerId, but POST /datasets
        // doesn't return it, it's purely in-memory construct, we have to patch it here
        newDataset.connectionName = dataset.connectionName
        newDataset.providerId = dataset.providerId

        // after we created new dataset, we need to update layers.config.dataId with ids received from backend
        const localDataId = dataset.id
        const newDataId = newDataset.id
        mapConfig.visState.layers = mapConfig.visState.layers.map((layer) =>
          layer.config.dataId === localDataId ? { ...layer, config: { ...layer.config, dataId: newDataId } } : layer
        )
        return newDataset
      })
    )
  ).filter((dataset: MapDataset | null): dataset is MapDataset => Boolean(dataset))
}

async function deleteUnusedDatasets({ mapId, migrationStatus }: { mapId: string; migrationStatus: MigrationStatus }) {
  return await Promise.all(
    Array.from(migrationStatus.entries()).map(async ([datasetId, migrationStatusValue]) => {
      const shouldDelete = migrationStatusValue === DatasetMigationStatus.Deleted

      if (shouldDelete) {
        await deleteMapDataset({ mapId, datasetId })
      }
    })
  )
}

function createKeplerLayerMock(storedLayer: CartoMapLayer) {
  const newConfig = {
    ...storedLayer.config
  }
  const visualChannels = visualChannelsConfigs

  for (const [channelKey, channelValue] of Object.entries(storedLayer.visualChannels)) {
    if (channelValue && typeof channelValue === 'object') {
      const visualChannelConfig = Object.values(visualChannelsConfigs).find((config) => config.field === channelKey)

      if (visualChannelConfig?.field) {
        newConfig[visualChannelConfig.field] = {
          id: channelValue.name,
          name: channelValue.name,
          type: 'string'
        }
      }
    }

    if (channelValue && typeof channelValue === 'string') {
      const visualChannelConfig = Object.values(visualChannelsConfigs).find((config) => config.scale === channelKey)
      if (visualChannelConfig?.scale) {
        newConfig[visualChannelConfig.scale] = channelValue
      }
    }
  }

  return {
    id: storedLayer.id,
    type: storedLayer.type,
    config: newConfig,
    visualChannels
  } as unknown as KeplerLayer
}

export function getQuadbinElevationZoomFactor(zoom: number) {
  // totally heuristic, not sure what is the correct factor
  // on zoom level 1, 0.5 is ok
  // on zoom level 2, 0.6 is ok
  // on zoom level 4, 0.5 is ok
  // on zoom level 11, 0.9 os ok
  // -> heuristic factor is: 0.057 * zoom + 0.273

  if (zoom < 4) return 0.5
  return 0.057 * zoom + 0.273
}

export function getHexagonElevationZoomFactor(zoom: number) {
  // again, totally heuristic, not sure what is the correct factor
  // 0.5 works on zoom 2
  // 0.6 works on zoom 4 (different ds !!!)
  // 0.33 works on zoom 4 (than this !!! )
  // 1 works on zoom 10
  // -> heuristic factor is: 0.112 * zoom - 0.118
  if (zoom <= 3) return 0.5
  if (zoom <= 4) return 0.6
  return 0.112 * zoom - 0.118
}
